import {
  Flex,
  PencilIcon,
  IconButton,
  Text,
  TrashIcon,
  Card,
} from '@buffer-mono/popcorn'
import { useState } from 'react'
import type { SavedReply } from '~publish/gql/graphql'
import styles from './SavedRepliesSettings.module.css'
import { useAddSavedReplyForm } from '~publish/pages/Comments/hooks/savedReplies/useSavedReplyForm'
import { DiscardDeleteSavedReplyDialog } from '~publish/pages/Comments/SavedReplyForm/DiscardDeleteSavedReplyDialog'
import { SavedReplyForm } from '~publish/pages/Comments/SavedReplyForm/SavedReplyForm'

export const SavedRepliesList = ({
  savedReplies,
}: {
  savedReplies: SavedReply[]
}): JSX.Element => {
  return (
    <Flex direction="column" gap="xs" fullWidth>
      {savedReplies.map((savedReply) => (
        <SavedReplyItem key={savedReply.id} savedReply={savedReply} />
      ))}
    </Flex>
  )
}

/**
 *
 * SAVED REPLY ITEM: Title and Text and Actions
 *
 */
const SavedReplyItem = ({
  savedReply,
}: {
  savedReply: SavedReply
}): JSX.Element => {
  const [editOpen, setEditOpen] = useState(false)
  return (
    <>
      {!editOpen && (
        <Card
          key={savedReply.id}
          padding="sm"
          className={styles.savedReplyItem}
        >
          <Flex
            direction="row"
            gap="2xs"
            align="center"
            justify="between"
            fullWidth
            className={styles.savedReplyItem}
          >
            <SavedReplyContent savedReply={savedReply} />
            <SavedReplyActions
              savedReply={savedReply}
              setEditOpen={setEditOpen}
            />
          </Flex>
        </Card>
      )}
      {/* Edit form */}
      <SavedReplyForm
        edit={true}
        open={editOpen}
        setOpen={setEditOpen}
        savedReply={savedReply}
      />
    </>
  )
}

/**
 *
 * CONTENT: Title and Text
 */
const SavedReplyContent = ({
  savedReply,
}: {
  savedReply: SavedReply
}): JSX.Element => {
  return (
    <Flex direction="column" gap="2xs" className={styles.savedReplyContent}>
      <Text weight="bold" className={styles.title} truncate>
        {savedReply.title}
      </Text>
      <Text className={styles.content} truncate>
        {savedReply.content.text}
      </Text>
    </Flex>
  )
}

/**
 *
 * ACTIONS: Edit and Delete
 */
const SavedReplyActions = ({
  savedReply,
  setEditOpen,
}: {
  savedReply: SavedReply
  setEditOpen: (open: boolean) => void
}): JSX.Element => {
  const { onDelete } = useAddSavedReplyForm({ isEditMode: false })
  const [openDiscardDialog, setOpenDiscardDialog] = useState(false)

  return (
    <>
      <Flex direction="row" gap="sm">
        <IconButton
          label="Edit"
          tooltip="Edit saved reply"
          variant="tertiary"
          onClick={(): void => setEditOpen(true)}
        >
          <PencilIcon />
        </IconButton>
        <IconButton
          label="Delete"
          tooltip="Delete saved reply"
          variant="tertiary"
          onClick={(): void => setOpenDiscardDialog(true)}
        >
          <TrashIcon color="critical" />
        </IconButton>
      </Flex>
      <DiscardDeleteSavedReplyDialog
        open={openDiscardDialog}
        onOpenChange={setOpenDiscardDialog}
        savedReply={savedReply}
        onCancelDeleting={(): void => setOpenDiscardDialog(false)}
        onKeepDeleting={(): Promise<void> => onDelete(savedReply.id)}
      />
    </>
  )
}
